import BrandingOverlay from "@components/display/BrandingOverlay/BrandingOverlay";
import { useAuth } from "@context/auth-context";
import { checkIsBusinessUser } from "@util/user.util";
import React, {
    createContext, ReactNode, useCallback, useContext, useMemo, useState,
} from "react";

const DEFAULT_LOGO = "/assets/images/fioh-text-logo.png";
const DEFAULT_DURATION = 1200;

type ShowOptions = {
    duration?: number;
    onComplete?: () => void;
};

interface BrandingOverlayContextProps {
    show: (options?: ShowOptions) => void;
}

const BrandingOverlayContext = createContext<BrandingOverlayContextProps | undefined>(undefined);

export const BrandingOverlayProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { authState } = useAuth();
    const { user, business } = authState;
    const [visible, setVisible] = useState(false);

    const show = useCallback(({ duration = DEFAULT_DURATION, onComplete }: ShowOptions = {}) => {
        setVisible(true);

        setTimeout(() => {
            setVisible(false);
            onComplete?.();
        }, duration);
    }, []);

    const logoSrc = (checkIsBusinessUser(user) && business?.logo) ? business.logo : DEFAULT_LOGO;

    const value = useMemo(() => ({ show }), [show]);

    return (
        <BrandingOverlayContext.Provider value={value}>
            {children}
            <BrandingOverlay show={visible} logoSrc={logoSrc} />
        </BrandingOverlayContext.Provider>
    );
};

export const useBrandingOverlay = () => {
    const context = useContext(BrandingOverlayContext);

    if (!context) {
        throw new Error("useBrandingOverlay must be used within a BrandingOverlayProvider");
    }

    return context;
};
